import { useCallback, useEffect, useRef, useState } from "react";

import { buildStreamURL, exchangeStreamToken } from "src/api/client";
import type {
  CancellationReason,
  PositionUpdateEvent,
  QueueStatus,
  StatusChangedEvent,
} from "src/api/types";

export type ConnectionState = "connecting" | "connected" | "reconnecting" | "terminal" | "error";

export interface WaitingState {
  status: QueueStatus | null;
  cancellationReason: CancellationReason | null;
  connectionState: ConnectionState;
  patientsAhead: number | null;
  estimatedWaitMinutes: number | null;
  lastEventAt: number | null;
}

export function useWaitingSSE(clientId: string, streamToken: string): WaitingState {
  const [state, setState] = useState<WaitingState>({
    status: null,
    cancellationReason: null,
    connectionState: "connecting",
    patientsAhead: null,
    estimatedWaitMinutes: null,
    lastEventAt: null,
  });

  const esRef = useRef<EventSource | null>(null);
  const stoppedRef = useRef(false);
  const retryTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const connect = useCallback(
    async function run() {
      if (stoppedRef.current) return;
      if (!clientId || !streamToken) {
        setState((s) => ({ ...s, connectionState: "error" }));
        return;
      }

      setState((s) => ({ ...s, connectionState: s.status ? "reconnecting" : "connecting" }));

      try {
        const { sse_token } = await exchangeStreamToken(clientId, streamToken);
        if (stoppedRef.current) return;

        const es = new EventSource(buildStreamURL(sse_token));
        esRef.current = es;

        es.onopen = () => {
          setState((s) => ({ ...s, connectionState: "connected" }));
        };

        es.addEventListener("queue_entry.status_changed", (e: MessageEvent) => {
          const data = JSON.parse(e.data) as StatusChangedEvent;
          setState((s) => ({
            ...s,
            status: data.status,
            cancellationReason: data.cancellation_reason ?? null,
            connectionState: data.terminal ? "terminal" : "connected",
            lastEventAt: Date.now(),
          }));
          if (data.terminal) {
            stoppedRef.current = true;
            es.close();
          }
        });

        es.addEventListener("queue.position_update", (e: MessageEvent) => {
          const data = JSON.parse(e.data) as PositionUpdateEvent;
          setState((s) => ({
            ...s,
            patientsAhead: data.patients_ahead,
            estimatedWaitMinutes: data.estimated_wait_minutes,
            lastEventAt: Date.now(),
          }));
        });

        es.onerror = () => {
          es.close();
          esRef.current = null;
          if (!stoppedRef.current) {
            setState((s) => ({ ...s, connectionState: "reconnecting" }));
            retryTimeoutRef.current = setTimeout(() => void run(), 2000);
          }
        };
      } catch {
        if (!stoppedRef.current) {
          setState((s) => ({ ...s, connectionState: "error" }));
          retryTimeoutRef.current = setTimeout(() => void run(), 5000);
        }
      }
    },
    [clientId, streamToken],
  );

  useEffect(() => {
    stoppedRef.current = false;
    // Opens the stream; state is only set from its events.
    // oxlint-disable-next-line react-hooks/set-state-in-effect
    void connect();
    return () => {
      stoppedRef.current = true;
      esRef.current?.close();
      if (retryTimeoutRef.current) clearTimeout(retryTimeoutRef.current);
    };
  }, [connect]);

  return state;
}
